import * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTextingStepSave } from '../../hooks/useTextingStepSave';
import { useTextingStepState } from '../../hooks/useTextingStepState';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { TextingStepForm } from '../../components/TextingStepForm';
import { TextingStepAdvancedOptions } from '../../components/TextingStepAdvancedOptions';
import { ScheduleEditor } from '../../components/ScheduleEditor';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { useStepRenderer } from './hooks/useStepRenderer';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';
import { AUTOMATION_QUERY_KEYS } from '../../../../../queries/Automation/queryKeys';

interface TextingStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

export function TextingStepRenderer({ nodeId, data, readOnly: readOnlyProp }: TextingStepRendererProps) {
	const {
		step: textingStep,
		template,
		canEdit,
		readonly,
		trigger,
		errorMessages,
		stepData,
		stepId,
		impersonationContext,
	} = useStepRenderer<Api.ITextingAutomationStep>(data, readOnlyProp);

	const { updateNodeData } = useAutomationFlowContext();
	const stepType = data.stepType;
	const schedule = textingStep?.schedule || stepData?.schedule;
	const templateId = template?.id;
	const queryClient = useQueryClient();

	const {
		skipStepContactFilter,
		setSkipStepContactFilter,
		showRequiredFieldError,
		setShowRequiredFieldError,
		advancedOptionsToggled,
		setAdvancedOptionsToggled,
		autoSave,
	} = useTextingStepState({
		textingStep,
		template,
		readOnlyProp,
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const { save, onSaveStep, saveSenderOptions } = useTextingStepSave({
		textingStep,
		stepId,
		templateId,
		skipStepContactFilter,
		autoSave,
	});

	const syncStepToNodeData = (updatedFields: Partial<Api.ITextingAutomationStep>) => {
		if (!updateNodeData || !textingStep) return;
		updateNodeData(nodeId, {
			automationStep: { ...textingStep, ...updatedFields },
		});
	};

	// Wrap onSaveStep to also sync partial step data to the canvas node
	const handleSaveStep = async (
		stepToSave?: Partial<Api.ITextingAutomationStep>,
		newSchedule?: Api.IAutomationStepSchedule
	) => {
		await onSaveStep(stepToSave, newSchedule);
		if (stepToSave) {
			syncStepToNodeData(stepToSave);
		}
	};

	const handleSaveSenderOptions = (option: Api.ISendFromOptions, sendToSender: boolean) => {
		saveSenderOptions(option, sendToSender);
		syncStepToNodeData({ sendFromOptions: option });
	};

	const handleFilterChange = (filter: Api.IContactsFilterRequest | null) => {
		setSkipStepContactFilter(filter);
		save({ newContactFilter: filter });
		syncStepToNodeData({ contactFilter: filter ?? undefined });
	};

	const handleScheduleChange = (newSchedule: Api.IAutomationStepSchedule | null) => {
		if (!newSchedule) return;
		save({ newSchedule });
		syncStepToNodeData({ schedule: newSchedule });
	};

	// Refresh template data after conditional step changes (add/remove fallback email)
	const handleStepChanged = useCallback(() => {
		if (templateId) {
			queryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.template(templateId) });
		}
	}, [queryClient, templateId]);

	// Callback for the presentation layer to push updated step data back to the canvas node.
	// Matches the legacy list-editor pattern (HwcAutomationStepCardContent.updateStep).
	const handleUpdateStep = useCallback(
		(_stepId: string, updater: (prev: Api.IAutomationStep) => Api.IAutomationStep) => {
			if (!updateNodeData || !textingStep) return;
			const updated = updater(textingStep as Api.IAutomationStep);
			updateNodeData(nodeId, { automationStep: updated });
		},
		[updateNodeData, nodeId, textingStep]
	);

	if (!textingStep) {
		return <div>Loading step data… Try collapsing and re-expanding this node.</div>;
	}

	return (
		<>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='detailed'
				lastSavedAt={autoSave.lastSavedAt}
				errorMessage={autoSave.errorMessage}
			/>
			{textingStep && trigger && (
				<ScheduleEditor
					schedule={schedule}
					trigger={trigger}
					stepType={stepType}
					canEdit={canEdit}
					isBusy={autoSave.saveStatus === 'saving'}
					step={textingStep}
					index={0}
					onScheduleChange={handleScheduleChange}
				/>
			)}
			<TextingStepForm
				step={textingStep}
				trigger={trigger}
				schedule={schedule}
				readonly={readonly}
				showRequiredFieldError={showRequiredFieldError}
				onSaveStep={handleSaveStep}
				onClearError={() => setShowRequiredFieldError(false)}
				templateId={templateId}
				stepId={stepId}
				impersonationContext={impersonationContext}
				onStepChanged={handleStepChanged}
				updateStep={handleUpdateStep}
			/>
			<TextingStepAdvancedOptions
				step={textingStep}
				stepId={stepId || nodeId}
				nodeId={nodeId}
				filter={skipStepContactFilter}
				canEdit={canEdit}
				isExpanded={advancedOptionsToggled}
				onToggle={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
				onSaveSenderOptions={handleSaveSenderOptions}
				onFilterChange={handleFilterChange}
				impersonationContext={impersonationContext}
			/>
		</>
	);
}
